var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isAddress } from '@ethersproject/address';
import { LifiErrorCode } from '@lifi/sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Big from 'big.js';
import { useWatch } from 'react-hook-form';
import { useDebouncedWatch, useToken } from '.';
import { SwapFormKey, useLiFi, useWallet, useWidgetConfig } from '../providers';
import { useSettings } from '../stores';
const refetchTime = 60000;
export const useSwapRoutes = () => {
    var _a, _b;
    const lifi = useLiFi();
    const { variant, sdkConfig } = useWidgetConfig();
    const { account, provider } = useWallet();
    const queryClient = useQueryClient();
    const { slippage, enabledBridges, enabledExchanges, routePriority } = useSettings([
        'slippage',
        'routePriority',
        'enabledBridges',
        'enabledExchanges',
    ]);
    const [fromChainId, fromTokenAddress, toChainId, toTokenAddress, toAddress] = useWatch({
        name: [
            SwapFormKey.FromChain,
            SwapFormKey.FromToken,
            SwapFormKey.ToChain,
            SwapFormKey.ToToken,
            SwapFormKey.ToAddress,
        ],
    });
    const [fromTokenAmount] = useDebouncedWatch([SwapFormKey.FromAmount], 320);
    const { token: fromToken } = useToken(fromChainId, fromTokenAddress);
    const { token: toToken } = useToken(toChainId, toTokenAddress);
    const isEnabled = 
    // Boolean(account.address) &&
    !isNaN(fromChainId) &&
        !isNaN(toChainId) &&
        Boolean(fromToken === null || fromToken === void 0 ? void 0 : fromToken.address) &&
        Boolean(toToken === null || toToken === void 0 ? void 0 : toToken.address) &&
        !isNaN(fromTokenAmount) &&
        Number(fromTokenAmount) > 0 &&
        !Number.isNaN(slippage);
    const queryKey = [
        'routes',
        account.address,
        fromChainId,
        fromToken === null || fromToken === void 0 ? void 0 : fromToken.address,
        fromTokenAmount,
        toChainId,
        toToken === null || toToken === void 0 ? void 0 : toToken.address,
        toAddress,
        slippage,
        enabledBridges,
        enabledExchanges,
        routePriority,
        variant,
        (_a = sdkConfig === null || sdkConfig === void 0 ? void 0 : sdkConfig.defaultRouteOptions) === null || _a === void 0 ? void 0 : _a.allowSwitchChain,
    ];
    const previousDataUpdatedAt = (_b = queryClient.getQueryState(queryKey)) === null || _b === void 0 ? void 0 : _b.dataUpdatedAt;
    const refetchInterval = previousDataUpdatedAt
        ? Math.min(Math.abs(refetchTime - (Date.now() - previousDataUpdatedAt)), refetchTime)
        : refetchTime;
    const { data, isLoading, isFetching, isFetched, dataUpdatedAt, refetch } = useQuery(queryKey, ({ queryKey: [_, fromAddress, fromChainId, fromTokenAddress, fromTokenAmount, toChainId, toTokenAddress, toAddress, slippage, enabledBridges, enabledExchanges, routePriority, variant, allowSwitchChain,], signal, }) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        let toWalletAddress;
        try {
            toWalletAddress =
                (_c = (yield (provider === null || provider === void 0 ? void 0 : provider.resolveName(toAddress)))) !== null && _c !== void 0 ? _c : (isAddress(toAddress) ? toAddress : fromAddress);
        }
        catch (_e) {
            toWalletAddress = isAddress(toAddress) ? toAddress : fromAddress;
        }
        const fromAmount = Big(fromTokenAmount)
            .mul(Math.pow(10, ((_d = fromToken === null || fromToken === void 0 ? void 0 : fromToken.decimals) !== null && _d !== void 0 ? _d : 0)))
            .toString();
        const formattedSlippage = parseFloat(slippage) / 100;
        return lifi.getRoutes({
            fromChainId,
            fromAmount,
            fromTokenAddress,
            toChainId,
            toTokenAddress,
            fromAddress,
            toAddress: toWalletAddress,
            options: {
                slippage: formattedSlippage,
                bridges: {
                    allow: enabledBridges,
                },
                exchanges: {
                    allow: enabledExchanges,
                },
                order: routePriority,
                allowSwitchChain: variant === 'refuel' ? false : allowSwitchChain,
            },
        }, { signal });
    }), {
        enabled: isEnabled,
        refetchInterval,
        staleTime: refetchTime,
        cacheTime: refetchTime,
        retry(failureCount, error) {
            if ((error === null || error === void 0 ? void 0 : error.code) === LifiErrorCode.NotFound) {
                return false;
            }
            return true;
        },
    });
    return {
        routes: data === null || data === void 0 ? void 0 : data.routes,
        isLoading: isEnabled && isLoading,
        isFetching,
        isFetched,
        dataUpdatedAt,
        refetchTime,
        refetch,
    };
};
